import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Card,
  createStyles,
  Grid,
  Group,
  Progress,
  Stack,
  Text,
  Tooltip
} from '@mantine/core'
import {
  IconAlertCircle,
  IconArrowRight,
  IconBuildingCommunity,
  IconClock,
  IconHourglass,
  IconSun,
  IconTrash,
  IconUser
} from '@tabler/icons'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'

import { formatDuration } from '@/components/surfaces/CourseCard'
import { getApplicationStageStep, getApplicationStepPath, getApplicationSteps } from '@/lib/hooks'
import { EnrollmentApplication as EnrollmentApplicationType } from '@/types'

interface Props {
  application: EnrollmentApplicationType
  onContinue?: () => void
  onRemove?: (application: EnrollmentApplicationType) => void
}

const useStyles = createStyles(theme => ({
  card: {
    borderRadius: theme.radius.lg,
    borderColor: theme.colors.gray[3],
    overflow: 'visible'
  },
  image: {
    position: 'relative',
    width: 64,
    height: 64,
    flexShrink: 0,
    overflow: 'hidden',
    borderRadius: theme.radius.md,
    backgroundColor: theme.colors[theme.primaryColor][0]
  },
  stage: {
    padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
    borderRadius: theme.radius.md,
    backgroundColor: theme.colors.gray[0]
  }
}))

const formatTime = (time?: string | null) => {
  const [hours, minutes] = (time || '').split(':')
  return hours ? `${hours}h${minutes && minutes !== '00' ? minutes : ''}` : ''
}

const getStages = (application: EnrollmentApplicationType) => {
  const { uid, availableStages, currentStage, formResponse } = application || {}
  const steps = getApplicationSteps(
    uid,
    !!availableStages?.includes('institution_contract'),
    !!formResponse
  )
  const currentKey = getApplicationStageStep(currentStage)
  const currentIndex = Math.max(
    steps.findIndex(step => step.key === currentKey),
    0
  )

  return {
    current: steps[currentIndex],
    progress: Math.round(((currentIndex + 1) / steps.length) * 100)
  }
}

export default function ApplicationCard({ application, onContinue, onRemove }: Props) {
  // Hooks
  const { classes, theme } = useStyles()

  // Constants
  const { classGroup, currentStage, offering, studentSiteProfile: student } = application
  const { course } = offering || {}
  const { current: currentStep, progress } = getStages(application)
  const continueUrl =
    getApplicationStepPath(application.uid, getApplicationStageStep(currentStage)) || ''
  const isOfferingUnavailable = !application.offeringIsBrowsable

  const schedule = [formatTime(classGroup?.startTime), formatTime(classGroup?.endTime)]
    .filter(Boolean)
    .join(' às ')

  const infos = [
    {
      key: 'classGroup',
      Icon: IconBuildingCommunity,
      label: 'Turma',
      value: classGroup?.name
    },
    {
      key: 'period',
      Icon: IconSun,
      label: 'Turno',
      value: classGroup?.periodDisplay
    },
    {
      key: 'duration',
      Icon: IconHourglass,
      label: 'Duração',
      value: formatDuration(course?.duration, course?.durationType)
    },
    {
      key: 'schedule',
      Icon: IconClock,
      label: 'Horário',
      value: schedule
    }
  ].filter(info => !!info.value)

  // Render
  return (
    <Card withBorder p="md" className={classes.card}>
      <Stack spacing="md">
        <Group spacing="sm" align="flex-start" position="apart" noWrap>
          <Group spacing="sm" noWrap>
            <Box className={classes.image}>
              <Image
                src={course?.image || '/images/placeholders/store.png'}
                alt={course?.name || ''}
                fill
                sizes="64px"
                style={{ objectFit: 'cover' }}
              />
            </Box>
            <Text size="lg" weight={600} lh={1.3}>
              {course?.name}
            </Text>
          </Group>
          <Tooltip label="Cancelar inscrição" withArrow>
            <ActionIcon color="red" onClick={() => onRemove?.(application)}>
              <IconTrash size={20} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Box className={classes.stage}>
          <Group position="apart" mb={6} noWrap>
            <Group spacing="xs" noWrap>
              <Text size="xs" color="dimmed">
                Etapa atual
              </Text>
              <Text size="sm" weight={600}>
                {currentStep?.label}
              </Text>
            </Group>
            <Text size="xs" weight={700} color="teal">
              {progress}%
            </Text>
          </Group>
          <Progress value={progress} color="teal" size="sm" radius="xl" />
        </Box>

        {!!student && (
          <Box>
            <Group spacing={6} noWrap>
              <IconUser size={14} color={theme.colors[theme.primaryColor][7]} />
              <Text size="xs" color="dimmed">
                Aluno
              </Text>
            </Group>
            <Text size="sm" weight={600} mt={2}>
              {`${student.firstName} ${student.lastName}`}
            </Text>
          </Box>
        )}

        {!!infos.length && (
          <Grid gutter="sm">
            {infos.map(info => (
              <Grid.Col key={info.key} xs={12} sm={6}>
                <Group spacing={6}>
                  <info.Icon size={14} color={theme.colors[theme.primaryColor][7]} />
                  <Text size="xs" color="dimmed">
                    {info.label}
                  </Text>
                </Group>
                <Text size="sm" weight={600} mt={2}>
                  {info.value}
                </Text>
              </Grid.Col>
            ))}
          </Grid>
        )}

        {isOfferingUnavailable && (
          <Alert icon={<IconAlertCircle size={16} />} color="orange" p="xs">
            Esta oferta não está mais disponível para novas matrículas.
          </Alert>
        )}

        {isOfferingUnavailable ? (
          <Button size="md" radius="xl" fullWidth rightIcon={<IconArrowRight size={16} />} disabled>
            Continuar matrícula
          </Button>
        ) : (
          <Button
            component={Link}
            href={continueUrl}
            size="md"
            radius="xl"
            fullWidth
            rightIcon={<IconArrowRight size={16} />}
            onClick={onContinue}>
            Continuar matrícula
          </Button>
        )}
      </Stack>
    </Card>
  )
}
